import { useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AnalystButton } from '../components/Analyst/AnalystButton';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client.js';
import type {
  DeliveryLeaderRow,
  DeliveryManagerData,
  DeliveryOverviewData,
  DeliveryRecruiterData,
  DeliveryRecruiterRow,
  DeliveryScopeType,
  Envelope,
  Grain,
  PeriodKey,
  RecruiterOffering,
  Source,
  Specialty,
  Targeting,
} from '../api/types.js';
import {
  DeliveryManagerTable,
  DeliveryRecruiterTable,
} from '../components/DeliveryTable/DeliveryTable.js';
import { EntityHeader } from '../components/EntityHeader/EntityHeader.js';
import { ExportButton } from '../components/ExportButton/ExportButton.js';
import { LeaderPills } from '../components/LeaderPills/LeaderPills.js';
import { LineChart } from '../components/LineChart/LineChart.js';
import { SegmentedControl } from '../components/SegmentedControl/SegmentedControl.js';
import {
  EmptyState,
  ErrorBanner,
  NoSnapshotState,
  Skeleton,
  useReservedHeight,
} from '../components/States/States.js';
import type { QueryDescriptor } from '../store/dataStore.js';
import { queries } from '../store/queries.js';
import { useQuery } from '../store/StoreProvider.js';
import { Header } from '../shell/Header.js';
import {
  DELIVERY_CHART_DEFAULTS,
  DELIVERY_DEFAULTS,
  comboKey,
  useChartFilters,
  useDeliveryFilters,
} from '../state/filters.js';
import {
  LEADER_PARAM,
  canonicalLeaderKeys,
  leaderSelectionPath,
  leaderSetKey,
  normalizeLeaderKey,
} from '../state/leaderSet.js';
import { color, featuredPanel, fontSize, radius } from '../theme/tokens.js';

/**
 * Delivery — the org view (FR-D07 – FR-D16, PRD-Delivery §6).
 *
 * **One page, four states.** The chart, the tile strip, and the header adapt to the scope; only
 * the drill-down section changes shape:
 *
 * ```text
 * /delivery                          every manager in the business
 * /delivery/leader/:leaderKey        that leader's managers
 * /delivery?leader=A&leader=B        two or more leaders' managers, each once (FR-D07a)
 * /delivery/manager/:managerId       that manager's recruiters
 * /delivery/recruiter/:recruiterId   that recruiter's book detail
 * ?focus=:recruiterId                on a manager URL — scopes the CHART, keeps the table
 * ```
 *
 * Each state has its own address, which is what makes a person shareable and re-openable without
 * a people search (FR-D11, FR-D16). **An address naming an unknown or out-of-scope entity renders
 * an explanation, never a redirect to the unfiltered page** — a redirect would answer a question
 * the user did not ask with numbers that look like an answer.
 *
 * ## Three separate filter stores, deliberately
 *
 * | Control | Governs | Must not touch |
 * | ------- | ------- | -------------- |
 * | Chart source / targeting / specialty | the chart **and the tile strip** | the table |
 * | Chart grain | the chart alone | the strip, the table |
 * | Table source / targeting / specialty / period | the table's columns | the chart, the strip |
 * | Nursing / Allied (manager state) | which **rows** are listed | the chart (FR-D13) |
 *
 * ⚠ **The first row is an amendment** (owner decision 2026-09-03, amending PRD-Delivery §11.6,
 * which read "changing a chart filter moves the chart and nothing else"). The chart and the strip
 * are **one card** now, and a lens applied to the line while the headline figures above it held
 * still made the two halves of that card answer different questions. The strip reads
 * `tilesByCombo[the chart's combo]` — the server's own precomputed block for that combination,
 * so switching lens is a lookup and no tile can disagree with what the API would have returned.
 * Three things bound it:
 *
 * - **Grain is not part of the lens.** Daily/Weekly/Monthly/Quarterly is the chart's x-axis; every
 *   figure on the strip is an as-of count, so there is nothing for a bucket width to change.
 * - **The period is still the table's.** Of the strip's figures only Conversion (offers ÷ subs)
 *   is period-scoped at all — the rest are as-of counts — and the Timeline control is the one
 *   that owns the period on this page.
 * - **The lens is named on the card face** (`Follows the chart · Internal · Target · Nursing`) and
 *   folded into each lensed tile's accessible name. On a card whose figures used to answer to
 *   nothing, an unlabelled number that moved when a chart control was touched reads as a fault.
 *
 * The last row is the one still worth stating out loud: it narrows a list of people, and the chart
 * has its own specialty filter. Wiring them together would make one control silently change two
 * things, and the chart is served by an endpoint that never sees this parameter at all.
 *
 * The **period governs the activity columns only** (FR-D12). Headcount and book columns are
 * as-of counts and do not move with it, which makes the totals row heterogeneous on purpose — so
 * the page says so beside the control rather than leaving it to be misread.
 *
 * ⚠ **Three absences here are load-bearing, not omissions.** No JOBS series (FR-D09), no people
 * search (FR-D11), no book-health flag (FR-D17). Each is deferred with recorded evidence.
 */

const SOURCE_OPTIONS = [
  { value: 'all' as Source, label: 'All' },
  { value: 'internal' as Source, label: 'Internal' },
];
const TARGETING_OPTIONS = [
  { value: 'all' as Targeting, label: 'All' },
  { value: 'target' as Targeting, label: 'Target' },
];
const SPECIALTY_OPTIONS = [
  { value: 'all' as Specialty, label: 'All' },
  { value: 'nursing' as Specialty, label: 'Nursing' },
  { value: 'allied' as Specialty, label: 'Allied' },
];
const GRAIN_OPTIONS = [
  { value: 'daily' as Grain, label: 'Daily' },
  { value: 'weekly' as Grain, label: 'Weekly' },
  { value: 'monthly' as Grain, label: 'Monthly' },
  { value: 'quarterly' as Grain, label: 'Quarterly' },
];
const PERIOD_OPTIONS: Array<{ value: PeriodKey; label: string; accessibleLabel: string }> = [
  { value: '30d', label: '30d', accessibleLabel: 'Period: last 30 days' },
  { value: '90d', label: '90d', accessibleLabel: 'Period: last 90 days' },
  { value: '6M', label: '6M', accessibleLabel: 'Period: 6 months' },
  { value: '12M', label: '12M', accessibleLabel: 'Period: 12 months' },
  { value: '24M', label: '24M', accessibleLabel: 'Period: 24 months' },
  { value: 'YTD', label: 'YTD', accessibleLabel: 'Period: year to date' },
];
const OFFERING_OPTIONS = [
  { value: 'all' as RecruiterOffering, label: 'All' },
  { value: 'nursing' as RecruiterOffering, label: 'Nursing' },
  { value: 'allied' as RecruiterOffering, label: 'Allied' },
];

/**
 * The chart's filter state, spelled out for the card face.
 *
 * Longer than the control labels on purpose: `All` on a segmented control is read against the
 * control's own name beside it, while the strip's caption stands alone and has to say *all what*.
 * `Grain` is deliberately absent — see the note on the filter table above.
 */
const SOURCE_LENS: Record<Source, string> = { all: 'All sources', internal: 'Internal' };
const TARGETING_LENS: Record<Targeting, string> = { all: 'All accounts', target: 'Target' };
const SPECIALTY_LENS: Record<Specialty, string> = {
  all: 'Nursing + Allied',
  nursing: 'Nursing',
  allied: 'Allied',
};

function lensPhrase(filters: { source: Source; targeting: Targeting; specialty: Specialty }) {
  return [
    SOURCE_LENS[filters.source],
    TARGETING_LENS[filters.targeting],
    SPECIALTY_LENS[filters.specialty],
  ].join(' · ');
}

/**
 * Where this page's view state is remembered for the session (`state/persist.ts`).
 *
 * Not the *selection*: which leader, manager or recruiter is open lives in the URL and nowhere
 * else, because an address on this page is the shareable, re-openable state (FR-D16). Restoring a
 * remembered drill-down over `/delivery` would make the same link show two different pages.
 */
const STORAGE_KEYS = {
  chart: 'delivery.chart',
  table: 'delivery.table',
} as const;

const PERIOD_NOTE =
  'Period applies to Subs, Ofrs, Strt and GM. Recruiter counts and book columns are as-of ' +
  'counts and do not move with it.';

/** The three drill-down payloads, read through their shared head. */
type DeliveryData = DeliveryOverviewData | DeliveryManagerData | DeliveryRecruiterData;

type ScopeParams = {
  type: DeliveryScopeType;
  leaderKey?: string;
  /** The `leaders` scope's key — the selected leader keys, sorted and newline-joined. */
  leaderSetKey?: string;
  managerId?: string;
  recruiterId?: string;
};

/**
 * ⚠ **A set of ONE is the leader state**, whichever spelling the address used. The pills never
 * write `?leader=X` alone (`leaderSelectionPath` gives one leader its path), but a hand-trimmed
 * address can, and the leaders route refuses a set of one — the leader route is the scope that
 * carries that leader's trend arrows and exact unique-candidate figure.
 */
function scopeFrom(
  params: { leaderKey?: string; managerId?: string; recruiterId?: string },
  search: URLSearchParams,
): ScopeParams {
  if (params.recruiterId) return { type: 'recruiter', recruiterId: params.recruiterId };
  if (params.managerId) return { type: 'manager', managerId: params.managerId };
  if (params.leaderKey) return { type: 'leader', leaderKey: params.leaderKey };
  const keys = canonicalLeaderKeys(search.getAll(LEADER_PARAM));
  if (keys.length === 1) return { type: 'leader', leaderKey: keys[0] };
  if (keys.length > 1) return { type: 'leaders', leaderSetKey: leaderSetKey(keys) };
  return { type: 'all' };
}

/**
 * Only active people are listed (owner request 2026-11-02), and the server no longer sends a former
 * recruiter in either list. ⚠ **This is for a STORED copy**: a payload the browser kept from before
 * that change still carries them for up to six hours (`TTL_MS`), and against `N of M` it reads
 * `5 of 4` — which is how it was found.
 */
function listedOnly(rows: readonly DeliveryRecruiterRow[]): DeliveryRecruiterRow[] {
  return rows.filter((row) => row.former !== true);
}

/** What the refusal calls the thing the address named. */
const REFUSED_NOUN: Record<DeliveryScopeType, string> = {
  all: 'page',
  leader: 'leader',
  leaders: 'selection of leaders',
  manager: 'manager',
  recruiter: 'recruiter',
};

export function Delivery() {
  const navigate = useNavigate();
  const routeParams = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const scope = scopeFrom(routeParams, searchParams);
  /**
   * Both stores are **session-persisted** (`state/persist.ts`). Leaving the page and coming back
   * used to reset every control to its default, so a filter combination someone had set up to
   * read a team had to be rebuilt on each visit; the defaults in PRD-Delivery §6.6 are what a
   * fresh session opens on, not something to re-impose on every navigation. The keys are
   * separate because the stores are separate, and for the reason they are separate (FR-D13).
   */
  const chart = useChartFilters(DELIVERY_CHART_DEFAULTS, STORAGE_KEYS.chart);
  const table = useDeliveryFilters(DELIVERY_DEFAULTS, STORAGE_KEYS.table);
  const combo = comboKey(table.filters);

  /** The chart focus inside the manager state — a fourth chart scope, not a fifth page state. */
  const focusedRecruiterId = scope.type === 'manager' ? searchParams.get('focus') : null;

  const tableParams = {
    period: table.filters.period,
    source: table.filters.source,
    targeting: table.filters.targeting,
    specialty: table.filters.specialty,
  };

  /**
   * One request per state, one hook.
   *
   * The four responses share a head — `scope`, `tiles`, `leaders`, and the filter echo — and
   * differ only in their drill-down payload, so they are read through that union and narrowed
   * once below. A hook per state would fetch three responses the page cannot show.
   */
  /**
   * Which descriptor is a **conditional, not four hooks**: `store/queries.ts` gives each scope its
   * own cache key, so re-entering a manager paints the copy the store already holds instead of
   * blanking to a skeleton. The prefetch fills the all-managers state, every leader and every
   * manager on sign-in; a recruiter's book is fetched on first view, because there are 135 of them
   * and each sits one click from a page that is already warm.
   */
  const descriptor: QueryDescriptor<Envelope<DeliveryData>> =
    scope.type === 'recruiter'
      ? queries.deliveryRecruiter(scope.recruiterId!, tableParams)
      : scope.type === 'manager'
        ? queries.deliveryManager(scope.managerId!, {
            ...tableParams,
            // Only the manager state sends this, and only the list reads it (FR-D13).
            recruiterOffering: table.filters.recruiterOffering,
          })
        : scope.type === 'leader'
          ? queries.deliveryLeader(scope.leaderKey!, tableParams)
          : scope.type === 'leaders'
            ? queries.deliveryLeaders(scope.leaderSetKey!, tableParams)
            : queries.delivery(tableParams);

  const request = useQuery(descriptor);

  // The chart's scope. A focused recruiter row narrows it without leaving the manager's table.
  const chartScope: { scopeType: DeliveryScopeType; scopeKey: string | null } =
    focusedRecruiterId
      ? { scopeType: 'recruiter', scopeKey: focusedRecruiterId }
      : scope.type === 'all'
        ? { scopeType: 'all', scopeKey: null }
        : {
            scopeType: scope.type,
            scopeKey:
              scope.leaderKey ??
              scope.leaderSetKey ??
              scope.managerId ??
              scope.recruiterId ??
              null,
          };

  const timeseries = useQuery(queries.deliveryTimeseries({ ...chartScope, ...chart.filters }));

  const envelope = request.data;
  const data = envelope?.data ?? null;
  const unattributedBooks = envelope?.dataQuality?.unattributedBooks ?? null;

  const overview =
    scope.type === 'all' || scope.type === 'leader' || scope.type === 'leaders'
      ? (data as DeliveryOverviewData | null)
      : null;
  const managerData = scope.type === 'manager' ? (data as DeliveryManagerData | null) : null;
  const unmanaged = overview?.unmanagedRecruiters ? listedOnly(overview.unmanagedRecruiters) : [];
  const team = managerData ? listedOnly(managerData.recruiters) : [];
  const recruiterData =
    scope.type === 'recruiter' ? (data as DeliveryRecruiterData | null) : null;

  /**
   * The pills, held across a refetch.
   *
   * A set of leaders is never prefetched (seven leaders make 120 of them), so every click that
   * builds a new one leaves this page with no data for a moment — and the pills used to drop to
   * skeletons with it, so the row a reader was clicking through vanished under the pointer and
   * came back. They are a navigation set that carries no figure, so the last list is the right
   * one to show until the new response lands: the same reason the chart holds its legend chips.
   */
  const heldLeaders = useRef<DeliveryLeaderRow[]>([]);
  if (data?.leaders?.length) heldLeaders.current = data.leaders;
  const leaders = data?.leaders ?? heldLeaders.current;

  /**
   * The selection, read off the ADDRESS rather than the response, so a pill lights the moment it
   * is pressed instead of when its division arrives. Normalised the server's way, so a link typed
   * in another case still lights its pill.
   */
  const selectedLeaders =
    scope.type === 'leader'
      ? [normalizeLeaderKey(scope.leaderKey)]
      : scope.type === 'leaders'
        ? scope.leaderSetKey!.split('\n')
        : [];

  /**
   * The strip, read at the **chart's** combination rather than the table's.
   *
   * `tilesByCombo` is the server's own assembly of the same six tiles at all twelve
   * combinations, so this is a lookup and not a second derivation — the tile a lens switch lands
   * on is byte-identical to what the endpoint would return for it. The fallback is the served
   * `tiles` (the table's combination): a browser can be holding a six-hour-old cached payload
   * from before the field existed, and a strip of dashes would be a worse answer than the
   * previous lens.
   */
  /**
   * Hold both sections' heights while a **table filter** refetches the page's one request.
   *
   * This page moves further than the others when that happens, because `data` going null swaps
   * the entity card for a plain chart panel *and* drops the drill-down table to a skeleton — the
   * card shrinks by the tile strip and the table by however many people are on it, in one frame.
   * Two reservations rather than one wrapping both, so a card that comes back a different size
   * does not hold the table's space open too.
   */
  const cardHeight = useReservedHeight(request.loading && !data);
  const drillHeight = useReservedHeight(request.loading && !data);

  const chartCombo = comboKey(chart.filters);
  const stripTiles = data ? (data.tilesByCombo?.[chartCombo] ?? data.tiles) : null;
  const lens = lensPhrase(chart.filters);

  function selectLeaders(leaderKeys: string[]) {
    // Deselecting the last pill is the all-managers view (FR-D07a).
    navigate(leaderSelectionPath(leaderKeys));
  }

  function toggleFocus(recruiterId: number) {
    const next = new URLSearchParams(searchParams);
    if (String(recruiterId) === focusedRecruiterId) next.delete('focus');
    else next.set('focus', String(recruiterId));
    setSearchParams(next, { replace: true });
  }

  function goBack() {
    const backTo = data?.scope.backTo;
    if (!backTo || backTo.type === 'all') {
      navigate('/delivery');
      return;
    }
    if (backTo.type === 'leader') {
      navigate(`/delivery/leader/${encodeURIComponent(backTo.key)}`);
      return;
    }
    navigate(`/delivery/manager/${encodeURIComponent(backTo.key)}`);
  }

  /**
   * The chart's accessible name and its data table's caption.
   *
   * The visible copy is drawn only when the chart's subject **differs** from the card's own
   * heading (`showTitle` below) — which is exactly the focused-recruiter case, so that is the
   * only branch that has to distinguish itself from the `<h2>` a few pixels above it.
   */
  const chartTitle = focusedRecruiterId
    ? `Chart · recruiter ${focusedRecruiterId}`
    : (data?.scope.name ?? 'Delivery');

  /**
   * A 422 on an entity address. Named rather than redirected (FR-D16), and worded to cover both
   * causes without claiming to know which: an id that names nobody, and — for exactly one manager
   * today — an id whose whole team sits outside the regions this page reports (FR-D03, R-D07).
   *
   * The status is tested, not merely the presence of an error. A refusal is the API's considered
   * answer that this entity is not on the page; a 500 or a dropped connection says nothing about
   * the entity, and now that a revalidation can fail behind a page already painted from cache,
   * treating either as a refusal would replace real figures with "nothing to show here". Those
   * get the retry banner instead.
   */
  const isRefused =
    scope.type !== 'all' && request.error instanceof ApiError && request.error.status === 422;

  /**
   * The chart, built once and placed in whichever card is rendered below.
   *
   * A variable rather than JSX inside both branches, because those two branches differ only in
   * whether the entity has arrived — duplicating four segmented controls across them is how one
   * copy ends up wired to a different store than the other.
   */
  const chartNode = (
    <>
      <LineChart
        data={timeseries.data?.data ?? null}
        /* The bucket windows in the hover bubble end at the sync, not at the as-of
           day's calendar edge, on the newest bucket (FR-087). */
        syncedAt={timeseries.data?.syncedAt ?? null}
        loading={timeseries.loading}
        title={chartTitle}
        /* The card already carries the entity's name as its heading, so the chart's own title
           row would print it a second time. `title` stays — it is the accessible name and the
           data table's caption — and only the visible copy is suppressed, and only where that
           heading is actually on screen saying the same thing: a focused recruiter is a different
           subject from the card's heading, and a card still waiting for its entity has no
           heading at all. */
        showTitle={!data || Boolean(focusedRecruiterId)}
        /* The plot or the same figures as a matrix, from the two glyphs at the end of the
           controls row (owner request 2026-09-11, extending PRD §6.2's toggle to this page).
           This card withheld it until now for a reason that reads as answered rather than
           overturned: the tile strip below is read at the chart's lens (PRD-Delivery §11.6),
           and swapping the plot away looked like leaving six lensed tiles with no visible line
           to be the lens of. What actually leaves the card in the table view is the *line* —
           the chart's Source / Targeting / Specialty controls sit in the header row above both
           views, and `Follows the chart · …` is printed on the card face between the two — so
           the lens is still stated and still steerable. No figure moves with it (FR-034): the
           strip, the drill-down table and the request are untouched, and it resets to the plot
           on a fresh page like every other view control (SC-010). */
        tableView
        titleAdornment={
          focusedRecruiterId ? (
            <button
              type="button"
              onClick={() => toggleFocus(Number(focusedRecruiterId))}
              style={{
                fontSize: fontSize.caption,
                fontWeight: 600,
                color: color.inkMuted,
                background: color.surfaceChip,
                border: `1px solid ${color.borderChip}`,
                borderRadius: radius.pill,
                padding: '2px 9px',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              ✕ clear
            </button>
          ) : null
        }
        controls={
          <>
            <SegmentedControl
              label="Chart source"
              options={SOURCE_OPTIONS}
              value={chart.filters.source}
              onChange={(v) => chart.set('source', v)}
            />
            <SegmentedControl
              label="Chart targeting"
              options={TARGETING_OPTIONS}
              value={chart.filters.targeting}
              onChange={(v) => chart.set('targeting', v)}
            />
            <SegmentedControl
              label="Chart specialty"
              options={SPECIALTY_OPTIONS}
              value={chart.filters.specialty}
              onChange={(v) => chart.set('specialty', v)}
            />
            <SegmentedControl
              label="Chart grain"
              options={GRAIN_OPTIONS}
              value={chart.filters.grain}
              onChange={(v) => chart.set('grain', v)}
            />
          </>
        }
      />

      {timeseries.error ? (
        <div style={{ marginTop: 12 }}>
          <ErrorBanner message="Could not load the chart." onRetry={timeseries.reload} />
        </div>
      ) : null}
    </>
  );

  return (
    <>
      <Header
        actions={<AnalystButton />}
        crumb="CYNET Health Commercial"
        title={data ? data.scope.name : 'Delivery'}
        asOf={envelope?.asOf ?? null}
        syncedAt={envelope?.syncedAt ?? null}
        syncStatus={envelope?.syncStatus ?? 'no_data'}
        staleness={envelope?.staleness ?? null}
        unattributedBooks={unattributedBooks}
      />

      <div style={{ padding: '22px 30px 60px' }}>
        {envelope && envelope.syncStatus === 'no_data' ? (
          // Never a page of zeros: on a first deploy those are indistinguishable from a real
          // collapse in the business (Principle III).
          <NoSnapshotState />
        ) : isRefused ? (
          <EmptyState
            title={`Nothing to show for this ${REFUSED_NOUN[scope.type]}`}
            detail={
              scope.type === 'manager'
                ? 'This address names a manager who is not on this page. Either the identifier is unknown, or every recruiter on their team sits outside the regions this page reports — which is the case for one manager today. Their figures are not zero; they are elsewhere.'
                : scope.type === 'recruiter'
                  ? 'This address names a recruiter who is not on this page. Either the identifier is unknown, or their alignment falls outside the regions this page reports.'
                  : scope.type === 'leaders'
                    ? 'At least one leader this address names is not in the current snapshot. A leader rename produces a new address, so an older link can stop resolving.'
                    : 'This address names a leader who is not in the current snapshot. A leader rename produces a new address, so an older link can stop resolving.'
            }
            action={
              <Link
                to="/delivery"
                style={{ color: color.nursing, fontSize: fontSize.bodyStrong, fontWeight: 600 }}
              >
                Back to all managers
              </Link>
            }
          />
        ) : (
          <>
            {request.error ? (
              <div style={{ marginBottom: 20 }}>
                <ErrorBanner
                  message={`Could not load Delivery. ${request.error.message}`}
                  onRetry={request.reload}
                />
              </div>
            ) : null}

            {/* ---- 1. Leader pills — the scope selector, present in every state ----

                Above the chart (owner request 2026-08-28), where PRD-Delivery §6.2 puts them
                below it. They are the one control on this page that re-scopes *everything*
                under them — the chart, the tile strip and the drill-down table all follow the
                selected leader — so they read as a question asked before the answer rather than
                as a legend for the chart they sit under. Note this is the opposite arrangement
                from Regions Overview, where FR-024 fixes chart → pills and the pills narrow the
                chart alone. */}
            <LeaderPills
              leaders={leaders}
              selected={selectedLeaders}
              onChange={selectLeaders}
              loading={request.loading && !data && leaders.length === 0}
            />

            {/* ---- 2. ONE card: the selected entity, its chart, and the strip the chart moves ----

                The chart used to be a featured card of its own with the entity card beneath it
                (owner request 2026-09-03 merged them). Two consequences worth knowing before
                splitting them again: the strip's figures now answer to the chart's Source /
                Targeting / Specialty, which is why the controls sit above them and why the lens
                is named on the face; and the chart is rendered *outside* the entity branch below,
                so a slow drill-down request cannot take the chart's own filters off screen. */}
            <div ref={cardHeight.ref} style={cardHeight.style}>
              {data ? (
                <EntityHeader
                  scope={data.scope}
                  tiles={stripTiles ?? data.tiles}
                  lens={lens}
                  featured
                  chart={chartNode}
                  onBack={data.scope.backTo ? goBack : undefined}
                >
                  {recruiterData ? <TargetAccounts accounts={recruiterData.accounts} /> : null}
                </EntityHeader>
              ) : (
                <section
                  aria-label="Delivery performance chart"
                  style={{
                    ...featuredPanel,
                    borderRadius: radius['6xl'],
                    // The entity card's own padding — the two branches are one card in two
                    // states, so a different gutter here would shift the chart sideways the
                    // moment the entity lands. 20 rather than 24 for the chart matrix's width
                    // budget; see `<EntityHeader>`.
                    padding: '20px 20px',
                    marginBottom: 16,
                  }}
                >
                  {chartNode}
                  {/* The strip is what waits, not the chart. Gated on `loading` rather than on
                    `!data`: after a failed request the banner above says what happened, and a
                    skeleton there would promise figures that are not coming. */}
                  {request.loading ? (
                    <div style={{ marginTop: 18 }}>
                      <Skeleton height={92} />
                    </div>
                  ) : null}
                </section>
              )}
            </div>

            {/* ---- 3. Drill-down ---- */}
            <section
              aria-label="Delivery drill-down"
              ref={drillHeight.ref}
              style={drillHeight.style}
            >
              {request.loading && !data ? (
                <Skeleton height={320} />
              ) : overview ? (
                overview.managers.length === 0 && unmanaged.length === 0 ? (
                  <EmptyState
                    title={
                      scope.type === 'leader' || scope.type === 'leaders'
                        ? `${overview.scope.name} ${
                            scope.type === 'leaders' ? 'have' : 'has'
                          } no managers in this snapshot`
                        : 'No managers in this snapshot'
                    }
                    detail="This is an explanation, not a row of zeros: no manager under this division has a team inside the regions this page reports."
                  />
                ) : (
                  <DeliveryManagerTable
                    rows={overview.managers}
                    unmanaged={unmanaged}
                    combo={combo}
                    totals={overview.totalsByCombo?.[combo] ?? overview.totals}
                    unattributedBooks={unattributedBooks}
                    onSelectManager={(managerId) => navigate(`/delivery/manager/${managerId}`)}
                    // A recruiter with no manager (2026-10-30) opens their own page — there is no
                    // team to drill into.
                    onSelectRecruiter={(recruiterId) => navigate(`/delivery/recruiter/${recruiterId}`)}
                    /*
                     * ⚠ **One control per row and one for the table, and they are not the same
                     * export.** A row's button covers that manager's team; the toolbar's covers
                     * the scope the table is listing — every manager in the business on
                     * `/delivery`, one leader's on `/delivery/leader/:key`. Summing the rows does
                     * NOT give the toolbar's file on a leader page either: two managers report to
                     * two leaders (FR-D05), so leaders' teams overlap and only a manager sum
                     * partitions the recruiter set.
                     */
                    onExportManager={(row) => (
                      <DeliveryExport
                        scopeType="manager"
                        scopeKey={String(row.managerId)}
                        name={row.name ?? `Manager ${row.managerId}`}
                        period={table.filters.period}
                      />
                    )}
                    onExportRecruiter={(row) => (
                      <DeliveryExport
                        scopeType="recruiter"
                        scopeKey={String(row.recruiterId)}
                        name={row.name ?? row.email}
                        period={table.filters.period}
                      />
                    )}
                    header={
                      <TableHeader
                        title="Managers"
                        // The recruiters with no manager are counted apart, so the list never
                        // reads as more managers than there are.
                        count={(() => {
                          const managers = overview.managers.length;
                          const base = `${managers} ${managers === 1 ? 'manager' : 'managers'}`;
                          return unmanaged.length > 0
                            ? `${base} · ${unmanaged.length} with no manager`
                            : base;
                        })()}
                        filters={table}
                        exportControl={
                          <DeliveryExport
                            // The page's own scope, straight off the response — `all` on
                            // /delivery, `leader` under a leader. Deriving it from the route a
                            // second time here is how the file comes to cover a scope the table
                            // is not showing.
                            scopeType={overview.scope.type}
                            scopeKey={overview.scope.key}
                            name={overview.scope.name}
                            period={table.filters.period}
                          />
                        }
                      />
                    }
                  />
                )
              ) : managerData ? (
                <DeliveryRecruiterTable
                  rows={team}
                  combo={combo}
                  totals={managerData.totalsByCombo?.[combo] ?? managerData.totals}
                  unattributedBooks={unattributedBooks}
                  onSelectRecruiter={toggleFocus}
                  onExportRecruiter={(row) => (
                    <DeliveryExport
                      scopeType="recruiter"
                      scopeKey={String(row.recruiterId)}
                      name={row.name ?? row.email}
                      period={table.filters.period}
                    />
                  )}
                  focusedRecruiterId={focusedRecruiterId ? Number(focusedRecruiterId) : null}
                  // A recruiter with an empty book is still LISTED, with `—` in every metric
                  // column. This message covers the offering filter matching nobody, which is a
                  // different fact again (FR-D22).
                  emptyMessage="No recruiters match this filter."
                  header={
                    <TableHeader
                      title="Recruiters"
                      // "N of M" is the listed team against its headcount. Former recruiters are
                      // in neither (owner request 2026-11-02): not listed, not in `Rec`.
                      count={`${team.length} of ${
                        managerData.tiles.recruiters?.value ?? '—'
                      }`}
                      filters={table}
                      exportControl={
                        /*
                         * ⚠ **The MANAGER's whole team, not the rows currently listed.** The
                         * Nursing/Allied control beside this narrows the *list* (FR-D13) and
                         * moves no figure; the file follows the manager's Subs figure, which is
                         * the unnarrowed one. A button that quietly exported half a team because
                         * a list filter was set would be the same class of surprise FR-034 is
                         * about, and the sheet's Offering column is how that split is applied
                         * against rows a reader can still see.
                         */
                        <DeliveryExport
                          scopeType={managerData.scope.type}
                          scopeKey={managerData.scope.key}
                          name={managerData.scope.name}
                          period={table.filters.period}
                        />
                      }
                      offering={
                        <SegmentedControl
                          // Narrows the LIST. It must not move the chart (FR-D13).
                          label="Recruiter offering"
                          options={OFFERING_OPTIONS}
                          value={table.filters.recruiterOffering}
                          onChange={(v) => table.set('recruiterOffering', v)}
                        />
                      }
                    />
                  }
                />
              ) : recruiterData ? (
                <RecruiterBook data={recruiterData} filters={table} />
              ) : null}

              {overview || managerData ? (
                <div
                  style={{
                    fontSize: fontSize.caption,
                    color: color.inkSubtle,
                    marginTop: 10,
                  }}
                >
                  {PERIOD_NOTE}
                </div>
              ) : null}
            </section>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

type Filters = ReturnType<typeof useDeliveryFilters>;

/**
 * One Delivery export control (FR-D27).
 *
 * A thin wrapper purely so the five call sites cannot disagree about the window: **every**
 * Delivery export is cut by the table's period, because every one of these controls lives on the
 * Managers or Recruiters table, whose activity columns are cut by that same Timeline control
 * (FR-D12). Passing `window` at each site would make the chart's grain a typo away.
 */
function DeliveryExport({
  scopeType,
  scopeKey,
  name,
  period,
}: {
  scopeType: DeliveryScopeType;
  scopeKey: string;
  name: string;
  period: PeriodKey;
}) {
  return (
    <ExportButton delivery={{ scopeType, scopeKey, name }} window={{ period }} />
  );
}

/**
 * The control row shared by the Managers and Recruiters tables.
 *
 * ⚠ **Exported for its test, not for reuse elsewhere.** `tests/controlrow.order.test.tsx` asserts
 * the one rule this row shares with Region Performance, By Client and both chart cards — the
 * export control is last — and that rule is only worth anything if something checks it.
 */
export function TableHeader({
  title,
  count,
  filters,
  offering,
  exportControl,
}: {
  title: string;
  count: string;
  filters: Filters;
  offering?: React.ReactNode;
  /**
   * The whole-table export (FR-D27) — every person the table lists, in one file.
   *
   * ⚠ **LAST on the row, after the Timeline** — the product-wide rule since 2026-09-13: the
   * export is the rightmost control on every control row (this one, Region Performance's, By
   * Client's and both chart cards'), because it is the only control that leaves the page and
   * everything shaping what it exports sits to its left. Two of the four used to put it
   * mid-row, so a reader who had learned where it lives on one table had to hunt on the next.
   *
   * It also keeps the file's period control visible immediately beside it, which is the reason
   * it is in this row at all rather than floating above the card.
   */
  exportControl?: React.ReactNode;
}) {
  return (
    <>
      <div style={{ fontSize: fontSize.control, fontWeight: 600, color: color.ink }}>
        {title}
      </div>
      <span className="mono" style={{ fontSize: fontSize.caption, color: color.inkFaint }}>
        {count}
      </span>
      <div
        style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        {offering}
        <SegmentedControl
          label="Table source"
          options={SOURCE_OPTIONS}
          value={filters.filters.source}
          onChange={(v) => filters.set('source', v)}
        />
        <SegmentedControl
          label="Table targeting"
          options={TARGETING_OPTIONS}
          value={filters.filters.targeting}
          onChange={(v) => filters.set('targeting', v)}
        />
        <SegmentedControl
          label="Table specialty"
          options={SPECIALTY_OPTIONS}
          value={filters.filters.specialty}
          onChange={(v) => filters.set('specialty', v)}
        />
        <SegmentedControl
          label="Timeline"
          options={PERIOD_OPTIONS}
          value={filters.filters.period}
          onChange={(v) => filters.set('period', v)}
        />
        {exportControl}
      </div>
    </>
  );
}

/**
 * The recruiter state's target accounts (PRD-Delivery §6.5), every tier the sheet carries —
 * Primary, Secondary and 3rd (owner ruling 2026-10-29).
 *
 * Each arrives as `null` where there is none, so the UI renders `—`. The wire never carries the
 * string "No Target Account" — absence and a real account name have to stay distinguishable.
 *
 * ⚠ **No health status line.** The blueprint puts one here; it is out of scope with recorded
 * evidence (FR-D17).
 */
function TargetAccounts({
  accounts,
}: {
  accounts: { primary: string | null; secondary: string | null; third?: string | null };
}) {
  const rows = [
    { label: 'PRIMARY', value: accounts.primary, dot: color.target },
    { label: 'SECONDARY', value: accounts.secondary, dot: color.colTarget },
    { label: '3RD', value: accounts.third ?? null, dot: color.inkMuted },
  ];

  return (
    <div
      style={{
        borderTop: `1px solid ${color.borderSubtle}`,
        paddingTop: 14,
        display: 'flex',
        gap: 32,
        flexWrap: 'wrap',
      }}
    >
      {rows.map((row) => (
        <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: row.dot,
              flex: 'none',
            }}
          />
          <div>
            <div style={{ fontSize: '10px', color: color.inkFaint, fontWeight: 600 }}>
              {row.label}
            </div>
            <div
              style={{
                fontSize: fontSize.bodyStrong,
                fontWeight: 600,
                color: row.value ? color.ink : color.inkFaint,
              }}
            >
              {row.value ?? '—'}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The recruiter book detail (PRD-Delivery §6.5, §9.6 D-08).
 *
 * Two tiles here answer to their own rules rather than to the Timeline control, and both say so
 * on their face:
 *
 * - **`Subs 30/90` keeps its own fixed windows.** The label states the window, so it cannot
 *   mislead; a tile that silently re-based on the period would.
 * - **`Avg assign · Ext` renders `—`.** It has no source, exactly as on the Region Detail summary
 *   bar, and FR-055 is precisely this case: the cell says "not recorded", not "zero".
 */
function RecruiterBook({ data, filters }: { data: DeliveryRecruiterData; filters: Filters }) {
  const block = data.metrics;
  const subs = block.subs?.value ?? null;

  const cells = [
    {
      label: 'Subs 30/90',
      full: 'Submissions over the last 30 and 90 days — fixed windows, independent of the Timeline control',
      value: `${block.subs?.d30 != null ? `${block.subs.d30}%` : '—'} / ${
        block.subs?.d90 != null ? `${block.subs.d90}%` : '—'
      }`,
    },
    {
      label: 'Subs',
      full: `Submissions in the selected period (${filters.filters.period})`,
      value: subs == null ? '—' : String(subs),
    },
    {
      label: 'Avg assign · Ext',
      full: 'Average assignment length and extension rate — not recorded in this snapshot',
      value: '—',
    },
  ];

  return (
    <div
      style={{
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius['6xl'],
        padding: '18px 20px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 18,
      }}
    >
      {cells.map((cell) => (
        <div key={cell.label}>
          <div
            style={{
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '.3px',
              color: color.inkFaint,
              textTransform: 'uppercase',
            }}
          >
            {cell.label}
          </div>
          <div
            className="mono"
            title={cell.full}
            aria-label={`${cell.full}: ${cell.value}`}
            style={{
              fontSize: fontSize.statValue,
              fontWeight: 600,
              color: color.ink,
              marginTop: 4,
            }}
          >
            {cell.value}
          </div>
        </div>
      ))}
    </div>
  );
}
